import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Package, TrendingUp, Ticket, ExternalLink, Truck, Award, MousePointerClick, ShoppingCart, Wallet, Percent } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { CreatorCodes } from "@/components/CreatorCodes";
import { AffiliateBox, LevelProgress } from "@/components/LevelProgressAndAffiliate";
import { CreatorProfile } from "@/components/CreatorProfile";
import { LOGO_URL, STATUS_ORDER, TIER_ORDER } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";

function Timeline({ status, t }) {
  const idx = STATUS_ORDER.indexOf(status);
  const steps = STATUS_ORDER.filter((s) => s !== "inactive");
  return (
    <div className="flex flex-col gap-0">
      {steps.map((s, i) => {
        const done = STATUS_ORDER.indexOf(s) <= idx && status !== "inactive";
        return (
          <div key={s} className="flex items-start gap-4">
            <div className="flex flex-col items-center">
              <div className={`h-3 w-3 rounded-full ${done ? "bg-white" : "bg-zinc-700"}`} />
              {i < steps.length - 1 && <div className={`w-px h-10 ${done ? "bg-white/40" : "bg-zinc-800"}`} />}
            </div>
            <span className={`text-sm pb-8 ${done ? "text-white" : "text-zinc-600"}`}>{t(`status.${s}`)}</span>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-aura-border bg-aura-surface p-5">
      <Icon size={18} className="text-zinc-500 mb-3" />
      <div className="text-2xl font-display font-bold text-white">{value}</div>
      <div className="text-xs uppercase tracking-[0.1em] text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

export default function CreatorDashboard() {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/creator/me").then(({ data }) => setC(data)).catch(() => toast.error(t("creator.loadError"))).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const doLogout = async () => { await logout(); navigate("/"); };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-white" /></div>;
  if (!c) return null;

  return (
    <div className="grain min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-2xl border-b border-white/10">
        <div className="mx-auto max-w-[1200px] px-5 md:px-8 h-16 flex items-center justify-between">
          <img src={LOGO_URL} alt="AURA+" className="h-9 w-auto mix-blend-screen opacity-95" />
          <button data-testid="creator-logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            <LogOut size={16} /> {t("creator.logout")}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 md:px-8 py-10 md:py-14" data-testid="creator-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("creator.area")}</p>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-2">{t("creator.hi")}, {c.first_name} 👋</h1>
          </div>
          <div className="flex items-center gap-3">
            {c.founding_member && (
              <span data-testid="founding-badge" className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300">
                <Award size={13} /> {t("creator.founding")}
              </span>
            )}
            {c.tier && (
              <span data-testid="tier-badge" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white">
                {t("tiers.levels")[TIER_ORDER.indexOf(c.tier)]?.label} · {t("tiers.levels")[TIER_ORDER.indexOf(c.tier)]?.name}
              </span>
            )}
            <StatusBadge status={c.status} />
          </div>
        </div>
        <p className="text-zinc-500 mb-10">{c.program}</p>

        {c.tier && (
          <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8 mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("tiers.current")}</span>
              <span className="font-display text-lg font-semibold text-white">{t("tiers.levels")[TIER_ORDER.indexOf(c.tier)]?.name}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(t("tiers.levels")[TIER_ORDER.indexOf(c.tier)]?.perks || []).map((p, i) => (
                <span key={i} className="rounded-full border border-aura-border px-3 py-1.5 text-xs text-zinc-300">{p}</span>
              ))}
            </div>
          </div>
        )}

        <div className="mb-8">
          <AffiliateBox creator={c} />
        </div>

        <h2 className="font-display text-lg font-semibold text-white mb-4">{t("cdash.perfTitle")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8" data-testid="performance-grid">
          <Stat icon={MousePointerClick} label={t("cdash.clicks")} value={(c.clicks || 0).toLocaleString()} />
          <Stat icon={ShoppingCart} label={t("cdash.ordersGen")} value={(c.orders_count || 0).toLocaleString()} />
          <Stat icon={TrendingUp} label={t("cdash.totalSales")} value={`€${(c.sales_generated || 0).toLocaleString()}`} />
          <Stat icon={Wallet} label={t("cdash.commEarned")} value={`€${(c.commissions || 0).toLocaleString()}`} />
          <Stat icon={Wallet} label={t("cdash.commPaid")} value={`€${(c.commissions_paid || 0).toLocaleString()}`} />
          <Stat icon={Percent} label={t("cdash.conversion")} value={c.clicks ? `${((c.orders_count || 0) / c.clicks * 100).toFixed(1)}%` : "—"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <LevelProgress creator={c} />
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.03] p-6 md:p-8 flex flex-col justify-center">
            <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("cdash.commAvailable")}</span>
            <div className="mt-2 font-display text-4xl font-bold text-emerald-300" data-testid="commission-available">€{Math.max((c.commissions || 0) - (c.commissions_paid || 0), 0).toLocaleString()}</div>
            <span className="mt-3 text-xs text-zinc-500">{t("creator.discount")}: <span className="font-mono text-zinc-300">{c.discount_code || "—"}</span></span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8">
            <h2 className="font-display text-lg font-semibold text-white mb-6">{t("creator.applicationStatus")}</h2>
            <Timeline status={c.status} t={t} />
          </div>

          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8">
              <div className="flex items-center gap-2 mb-5"><Truck size={18} className="text-zinc-400" /><h2 className="font-display text-lg font-semibold text-white">{t("creator.shipping")}</h2></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div><p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{t("creator.tracking")}</p><p className="text-white break-words">{c.tracking_number || "—"}</p></div>
                <div><p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{t("creator.shippedOn")}</p><p className="text-white">{c.shipped_date || "—"}</p></div>
                <div><p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{t("creator.deliveredOn")}</p><p className="text-white">{c.delivered_date || "—"}</p></div>
              </div>
              {c.address && <p className="mt-4 text-sm text-zinc-500">{t("creator.address")}: <span className="text-zinc-300">{c.address}</span></p>}
            </div>

            <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8">
              <h2 className="font-display text-lg font-semibold text-white mb-5">{t("creator.publishedContent")}</h2>
              {(c.content_links || []).length === 0 ? (
                <p className="text-sm text-zinc-500">{t("creator.noContent")}</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {c.content_links.map((l, i) => (
                    <li key={i}><a href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"><ExternalLink size={14} /> {l}</a></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <CreatorCodes />
        </div>

        <div className="mt-6">
          <CreatorProfile creator={c} onUpdated={setC} />
        </div>
      </main>
    </div>
  );
}
